if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.menuDrawer = document.querySelector('.menu-drawer');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        this.localizeCountryNames();
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', (event) => {
          event.preventDefault();
          const isOpen = !this.elements.panel.hasAttribute('hidden');
          if (isOpen || this.elements.button.getAttribute('aria-expanded') === 'true') {
            this.hidePanel();
            return;
          }
          this.openSelector();
        });

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a[data-value]').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      localizeCountryNames() {
        if (this.elements.input?.name !== 'country_code' || !window.Intl?.DisplayNames) return;

        const displayLocale = document.documentElement.lang?.split('-')[0] || 'en';
        const regionNames = new Intl.DisplayNames([displayLocale], { type: 'region' });

        this.querySelectorAll('.country[data-country-code]').forEach((countryLabel) => {
          const localizedName = regionNames.of(countryLabel.dataset.countryCode);
          if (localizedName) countryLabel.textContent = localizedName;
        });
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        this.toggleMenuDrawerCountryState(false);
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        const selectedValue = event.currentTarget.dataset.value;
        this.elements.input.value = selectedValue;
        this.syncLocaleForCountrySelection(form, selectedValue);
        if (form) form.submit();
      }

      syncLocaleForCountrySelection(form, countryCode) {
        if (this.elements.input?.name !== 'country_code' || !form) return;

        let syncedLocaleInput = form.querySelector('input[name="locale_code"][data-country-sync="true"]');
        const targetLocale = countryCode === 'TR' ? 'tr' : 'en';

        if (!syncedLocaleInput) {
          syncedLocaleInput = document.createElement('input');
          syncedLocaleInput.type = 'hidden';
          syncedLocaleInput.name = 'locale_code';
          syncedLocaleInput.dataset.countrySync = 'true';
          form.appendChild(syncedLocaleInput);
        }

        syncedLocaleInput.value = targetLocale;
      }

      openSelector() {
        this.elements.button.focus();
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide')) {
          if (this.header) this.header.preventHide = true;
        }
        this.toggleMenuDrawerCountryState(true);
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = Array.from(this.querySelectorAll('a[data-value]'));
        const visibleCountryCodes = new Set();

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue !== '');
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          const isVisible = countryName.indexOf(searchValue) > -1;
          item.parentElement.classList.toggle('hidden', !isVisible);

          if (isVisible) {
            const inHiddenPopularList =
              item.closest('.popular-countries') && popularCountries?.classList.contains('hidden');

            if (!inHiddenPopularList) {
              visibleCountryCodes.add(item.dataset.value);
            }
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountryCodes.size
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }

      toggleMenuDrawerCountryState(isOpen) {
        if (!this.menuDrawer) return;
        this.menuDrawer.classList.toggle('country-selector-open', isOpen);
      }
    }
  );
}

const COUNTRY_LOCALE_SYNC_KEY = 'gogus-country-locale-sync-v2';
const COUNTRY_LOCALE_SYNC_PENDING_CLASS = 'country-locale-sync-pending';

function syncCountryLocaleOnInitialLoad() {
  const root = document.documentElement;
  const countryInput = document.querySelector('localization-form input[name="country_code"]');
  const form = countryInput?.form;
  const countryCode = countryInput?.value.toUpperCase();
  const currentLocale = document.documentElement.lang?.split('-')[0].toLowerCase();
  const targetLocale = countryCode === 'TR' ? 'tr' : 'en';

  if (!form || !countryCode || !currentLocale) {
    root.classList.remove(COUNTRY_LOCALE_SYNC_PENDING_CLASS);
    return;
  }

  if (currentLocale === targetLocale) {
    sessionStorage.removeItem(COUNTRY_LOCALE_SYNC_KEY);
    root.classList.remove(COUNTRY_LOCALE_SYNC_PENDING_CLASS);
    return;
  }

  const syncAttempt = `${countryCode}:${currentLocale}:${targetLocale}`;
  if (sessionStorage.getItem(COUNTRY_LOCALE_SYNC_KEY) === syncAttempt) {
    root.classList.remove(COUNTRY_LOCALE_SYNC_PENDING_CLASS);
    return;
  }

  let localeInput = form.querySelector('input[name="locale_code"][data-country-sync="true"]');
  if (!localeInput) {
    localeInput = document.createElement('input');
    localeInput.type = 'hidden';
    localeInput.name = 'locale_code';
    localeInput.dataset.countrySync = 'true';
    form.appendChild(localeInput);
  }

  countryInput.value = countryCode;
  localeInput.value = targetLocale;
  sessionStorage.setItem(COUNTRY_LOCALE_SYNC_KEY, syncAttempt);
  form.submit();
}

if (!document.documentElement.classList.contains(COUNTRY_LOCALE_SYNC_PENDING_CLASS)) {
  syncCountryLocaleOnInitialLoad();
}
